import React from 'react';
import InteractiveTextViewer from './InteractiveTextViewer';
import PropTypes from 'prop-types';

const TitleInteractiveTextViewer = ({ 
    formattedText, 
    textId, 
    level = 1,
    style = {},
    onWordDisplayed = null,
    chunkOrder = null,
    anchorId = null,
    centerAlign = false
}) => {
    // Define heading styles for different levels
    const getHeadingStyle = (level) => {
        const baseStyle = {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 'bold',
            lineHeight: 1.2,
            color: '#000000',
            textAlign: centerAlign ? 'center' : 'left',
            margin: '0',
            scrollMarginTop: '20px'
        };

        switch (level) {
            case 1:
                return {
                    ...baseStyle,
                    fontSize: '3rem',
                    letterSpacing: '-0.02em',
                    marginBottom: '2rem'
                };
            case 2:
                return {
                    ...baseStyle,
                    fontSize: '2.5rem',
                    letterSpacing: '-0.01em',
                    marginBottom: '1.5rem'
                };
            case 3:
                return {
                    ...baseStyle,
                    fontSize: '2rem',
                    marginBottom: '1rem'
                };
            case 4:
                return {
                    ...baseStyle,
                    fontSize: '1.5rem',
                    marginBottom: '0.875rem'
                };
            case 5:
                return {
                    ...baseStyle,
                    fontSize: '1.25rem',
                    marginBottom: '0.75rem'
                };
            case 6:
                return {
                    ...baseStyle,
                    fontSize: '1.125rem',
                    marginBottom: '0.5rem'
                };
            default:
                return {
                    ...baseStyle,
                    fontSize: '1.5rem',
                    marginBottom: '1rem'
                };
        }
    };

    const headingStyle = getHeadingStyle(level);
    const mergedStyle = { ...headingStyle, ...style };

    // Generate a valid CSS className (fix the selector issue)
    const safeTextId = textId.replace(/[^a-zA-Z0-9]/g, '');
    const uniqueClassName = `title-h${level}-${safeTextId}-${Math.random().toString(36).substr(2, 9)}`;

    // Create the appropriate heading tag based on level
    const headingTagName = `h${Math.min(Math.max(level, 1), 6)}`;

    // Clean the formatted text if it already contains header tags to avoid nesting
    const cleanFormattedText = formattedText
        .replace(/<\/?h[1-6][^>]*>/gi, '') // Remove any existing header tags
        .replace(/<\/?p[^>]*>/gi, ''); // Remove any paragraph tags that might interfere

    return (
        <div id={anchorId} style={{ scrollMarginTop: '20px' }}>
            <style>
                {`
                    .${uniqueClassName} {
                        display: block;
                        width: 100%;
                    }
                    
                    /* Style only the heading tag itself, not its contents */
                    .${uniqueClassName} ${headingTagName} {
                        font-family: ${mergedStyle.fontFamily} !important;
                        font-weight: ${mergedStyle.fontWeight} !important;
                        line-height: ${mergedStyle.lineHeight} !important;
                        color: ${mergedStyle.color} !important;
                        text-align: ${mergedStyle.textAlign} !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        font-size: ${mergedStyle.fontSize} !important;
                        letter-spacing: ${mergedStyle.letterSpacing || 'normal'} !important;
                        margin-bottom: ${mergedStyle.marginBottom} !important;
                        scroll-margin-top: 20px !important;
                        border: none !important;
                        outline: none !important;
                        background: none !important;
                    }
                    
                    /* Style only the word spans in the heading, not affecting other UI elements */
                    .${uniqueClassName} ${headingTagName} .chp {
                        display: inline !important;
                        padding: 2px 1px !important;
                        border-radius: 4px !important;
                        transition: all 0.2s ease !important;
                        cursor: pointer !important;
                        font-family: inherit !important;
                        font-size: inherit !important;
                        font-weight: inherit !important;
                        line-height: inherit !important;
                        color: inherit !important;
                        text-align: inherit !important;
                        letter-spacing: inherit !important;
                        margin: 0 !important;
                        border: none !important;
                        background: transparent !important;
                    }
                    
                    .${uniqueClassName} ${headingTagName} .chp:hover {
                        background-color: rgba(25, 118, 210, 0.08) !important;
                        transform: translateY(-1px) !important;
                        box-shadow: 0 2px 4px rgba(25, 118, 210, 0.2) !important;
                    }
                    
                    /* Don't interfere with other InteractiveTextViewer elements */
                    .${uniqueClassName} .MuiButton-root,
                    .${uniqueClassName} .MuiGrid-root,
                    .${uniqueClassName} .MuiBox-root,
                    .${uniqueClassName} .MuiTypography-root {
                        font-family: inherit !important;
                        font-size: inherit !important;
                        font-weight: inherit !important;
                        color: inherit !important;
                    }
                    
                    /* Responsive adjustments */
                    @media (max-width: 768px) {
                        .${uniqueClassName} ${headingTagName} {
                            font-size: ${level === 1 ? '2.5rem' : level === 2 ? '2rem' : level === 3 ? '1.5rem' : '1.25rem'} !important;
                        }
                    }
                    
                    @media (max-width: 480px) {
                        .${uniqueClassName} ${headingTagName} {
                            font-size: ${level === 1 ? '2rem' : level === 2 ? '1.75rem' : level === 3 ? '1.25rem' : '1.125rem'} !important;
                        }
                    }
                `}
            </style>
            
            <div className={uniqueClassName}>
                {React.createElement(
                    headingTagName,
                    {
                        style: mergedStyle
                    },
                    <InteractiveTextViewer
                        formattedText={cleanFormattedText}
                        textId={textId}
                        baseClassName={`title-content-${safeTextId}`}
                        style={{
                            fontFamily: 'inherit',
                            fontSize: 'inherit',
                            fontWeight: 'inherit',
                            color: 'inherit',
                            lineHeight: 'inherit',
                            textAlign: 'inherit'
                        }}
                        showInteractiveStyles={true}
                        enableTranslation={false} // Disable translation for titles
                        source={{ 
                            type: 'title', 
                            id: textId, 
                            chunkOrder: chunkOrder,
                            level: level
                        }}
                        onWordDisplayed={onWordDisplayed}
                    />
                )}
            </div>
        </div>
    );
};

TitleInteractiveTextViewer.propTypes = {
    formattedText: PropTypes.string.isRequired,
    textId: PropTypes.string.isRequired,
    level: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
    style: PropTypes.object,
    onWordDisplayed: PropTypes.func,
    chunkOrder: PropTypes.number,
    anchorId: PropTypes.string,
    centerAlign: PropTypes.bool
};

export default TitleInteractiveTextViewer; 